import api from './api';
import { ApiResponse, PaginatedResponse, Document, DocumentHistory, Template, FieldMapping } from '../models';

export interface DocumentGenerateRequest {
  consultation_id: number;
  template_id: number;
}

// 통합 WeDMS 문서 타입 (녹음 + 생성 문서)
export interface WeDMSFile {
  id: number;
  type: 'recording' | 'document';
  filename: string;
  file_size: number;
  wedms_doc_id: string;
  wedms_status: 'pending' | 'processing' | 'completed' | 'failed' | null;  // WeDMS 처리 상태
  wedms_error: string | null;  // WeDMS 처리 실패 시 에러 메시지
  consultation_id: number | null;  // null for standalone recordings
  consultation_date: string | null;  // null for standalone recordings
  client_name: string;
  created_at: string;
  // Recording specific
  stt_status: string | null;
  stt_error: string | null;  // STT 실패 시 에러 메시지
  mime_type: string | null;
  // LLM 처리 관련 (Recording only)
  llm_status: 'pending' | 'processing' | 'completed' | 'failed' | null;
  llm_error: string | null;
  // Document specific
  template_name: string | null;
  version: number | null;
}

export const documentService = {
  async generate(data: DocumentGenerateRequest): Promise<Document> {
    const response = await api.post<ApiResponse<Document>>('/documents/generate/', data);
    return response.data.data;
  },

  async regenerate(id: number): Promise<Document> {
    const response = await api.post<ApiResponse<Document>>(`/documents/${id}/regenerate/`);
    return response.data.data;
  },

  async getList(consultationId?: number): Promise<Document[]> {
    const params = consultationId ? { consultation: consultationId } : {};
    const response = await api.get<ApiResponse<PaginatedResponse<Document>>>('/documents/', { params });
    // 페이지네이션 응답에서 results 추출
    return response.data.data.results;
  },

  async getById(id: number): Promise<Document> {
    const response = await api.get<ApiResponse<Document>>(`/documents/${id}/`);
    return response.data.data;
  },

  async getHistory(id: number): Promise<DocumentHistory[]> {
    const response = await api.get<ApiResponse<DocumentHistory[]>>(`/documents/${id}/history/`);
    return response.data.data;
  },

  async download(id: number): Promise<Blob> {
    const response = await api.get(`/documents/${id}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`/documents/${id}/`);
    return response.data;
  },

  // 통합 WeDMS 문서 목록 (녹음 파일 + 생성 문서)
  async getAllFiles(): Promise<WeDMSFile[]> {
    const response = await api.get<ApiResponse<{ count: number; results: WeDMSFile[] }>>('/documents/all-files/');
    return response.data.data.results;
  },

  // 문서 WeDMS 상태 확인
  async getWeDMSStatus(id: number): Promise<DocumentWeDMSStatus> {
    const response = await api.get<ApiResponse<DocumentWeDMSStatus>>(`/documents/${id}/wedms-status/`);
    return response.data.data;
  },
};

// 문서 WeDMS 상태 응답 타입
export interface DocumentWeDMSStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  wedms_doc_id: string | null;
  wedms_task_id: string | null;
  wedms_error: string | null;
}

// STT 상태 응답 타입
export interface STTStatusResponse {
  status: string;
  stt_text: string | null;
  stt_error: string | null;  // STT 실패 시 에러 메시지
  processed_at: string | null;
  wedms_doc_id: string | null;
  wedms_task_id?: string | null;
  task_status?: string;  // WeDMS task status: PENDING, STARTED, SUCCESS, FAILURE
  message?: string;
  error?: string;
}

// 녹음 파일 서비스
export const recordingService = {
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`/recordings/${id}/`);
    return response.data;
  },

  async download(id: number): Promise<Blob> {
    const response = await api.get(`/recordings/${id}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getSTTStatus(id: number): Promise<STTStatusResponse> {
    const response = await api.get<ApiResponse<STTStatusResponse>>(`/recordings/${id}/stt-status/`);
    return response.data.data;
  },

  // 상담 없이 녹음 파일만 업로드 (standalone)
  async uploadStandalone(file: File): Promise<{ id: number; filename: string; message: string }> {
    const formData = new window.FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<{ id: number; filename: string }>>('/recordings/upload-standalone/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return {
      id: response.data.data.id,
      filename: response.data.data.filename,
      message: response.data.message || '업로드 완료',
    };
  },

  // 녹음 파일을 상담에 연결
  async linkConsultation(recordingId: number, consultationId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post<ApiResponse<unknown>>(`/recordings/${recordingId}/link-consultation/`, {
      consultation_id: consultationId,
    });
    return {
      success: response.data.success,
      message: response.data.message || '연결 완료',
    };
  },
};

export const templateService = {
  async getList(includeInactive = false): Promise<Template[]> {
    const params = includeInactive ? {} : { is_active: true };
    const response = await api.get<ApiResponse<PaginatedResponse<Template>>>('/templates/', { params });
    // 페이지네이션 응답에서 results 추출
    return response.data.data.results;
  },

  async getById(id: number): Promise<Template> {
    const response = await api.get<ApiResponse<Template>>(`/templates/${id}/`);
    return response.data.data;
  },

  async upload(options: {
    name: string;
    description: string;
    previewFile?: File;      // 배경용 HWPX (PDF 미리보기용 원본)
    generatorFile?: File;    // 생성용 HWPX (<<__필드명__>> 플레이스홀더 포함)
    file?: File;             // 레거시: 단일 파일 업로드 (preview_file과 generator_file 둘 다 설정)
    image?: File;
    fieldMapping?: Record<string, unknown>;
  }): Promise<Template> {
    const formData = new window.FormData();
    formData.append('name', options.name);
    formData.append('description', options.description || '');

    // 듀얼 파일 업로드 지원
    if (options.previewFile) {
      formData.append('preview_file', options.previewFile);
    }
    if (options.generatorFile) {
      formData.append('generator_file', options.generatorFile);
    }
    // 레거시 단일 파일 (이전 버전 호환)
    if (options.file) {
      formData.append('file', options.file);
    }
    if (options.image) {
      formData.append('image', options.image);
    }
    if (options.fieldMapping) {
      formData.append('field_mapping', JSON.stringify(options.fieldMapping));
    }

    const response = await api.post<ApiResponse<Template>>('/templates/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  async update(id: number, data: Partial<Template>): Promise<Template> {
    const response = await api.patch<ApiResponse<Template>>(`/templates/${id}/`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/templates/${id}/`);
  },

  // PDF 미리보기 URL 반환 (DAS 백엔드에서 프록시)
  getPdfPreviewUrl(id: number): string {
    // DAS 백엔드를 통해 PDF를 프록시하므로 CORS 문제 없음
    const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
    return `${baseUrl}/templates/${id}/pdf-preview/`;
  },

  // 템플릿의 필드 매핑 업데이트 (새 API endpoint 사용)
  async updateFieldMapping(id: number, fieldMapping: FieldMapping): Promise<Template> {
    const response = await api.patch<ApiResponse<Template>>(`/templates/${id}/field-mapping/`, {
      field_mapping: fieldMapping,
    });
    return response.data.data;
  },

  // 템플릿 이미지 업로드
  async uploadImage(id: number, image: File): Promise<Template> {
    const formData = new window.FormData();
    formData.append('image', image);

    const response = await api.post<ApiResponse<Template>>(`/templates/${id}/upload-image/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // 템플릿 WeDMS 상태 확인
  async getWeDMSStatus(id: number): Promise<TemplateWeDMSStatus> {
    const response = await api.get<ApiResponse<TemplateWeDMSStatus>>(`/templates/${id}/wedms-status/`);
    return response.data.data;
  },
};

// 템플릿 WeDMS 상태 응답 타입
export interface TemplateWeDMSStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  wedms_doc_id: string | null;
  wedms_task_id: string | null;
  wedms_error: string | null;
  has_pdf_preview: boolean;
}
